import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useBeforeUnload } from 'react-use'
import { Box, IconButton, Typography } from '@mui/material'
import { Button, Icon } from 'src/components/atoms'
import BreadcrumbsPipe, { Path } from 'src/components/molecules/BreadcrumbsPipe'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import Modal from 'src/components/molecules/Modal'
import { Option } from 'src/components/molecules/SelectWithOption'
import { ToastTriggerContext } from 'src/context/toast.context'
import {
  EditOCRFormat as EditOCRFormatPayload,
  EditReadItem,
} from 'src/slices/editCalculations/editCalculationsSlice'
import { CalculationItem, CalculationDSL } from 'src/slices/services/api'
import { isEqual } from 'src/utils/array'
import EditOCRFormatTable, { BodyRow } from '../components/EditOCRFormatTable'
import EditOCRFormatFunctions, { RowFieldValue } from './function'
import styles from './styles.module.scss'

export type EditOCRFormatProps = {
  breadcrumbs: Path[]
  editReadItems: EditReadItem[]
  editOCRFormats: EditOCRFormatPayload[]
  calculationItems?: CalculationItem[]
  dsls?: CalculationDSL[]

  onClickGoForward: (ocrFormats: EditOCRFormatPayload[]) => void
  onClickGoBack: (ocrFormats: EditOCRFormatPayload[]) => void
  onClickConfirmClose: () => void
  onUpdateEditOCRFormats: (ocrFormats: EditOCRFormatPayload[]) => void
}

const EditOCRFormat: React.StyledFC<EditOCRFormatProps> = ({
  breadcrumbs,
  editReadItems,
  editOCRFormats,
  calculationItems,
  dsls,
  onClickGoForward,
  onClickGoBack,
  onClickConfirmClose,
  onUpdateEditOCRFormats,
}: EditOCRFormatProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const toastContext = useContext(ToastTriggerContext)
  const [rows, setRows] = useState<BodyRow[]>([])
  const [initialValues, setInitialValues] = useState<
    (string | number | boolean | undefined)[]
  >([])
  const formMethods = useForm<{ rows: RowFieldValue[] }>()

  useBeforeUnload(() => {
    const currentValues =
      formMethods.getValues().rows?.flatMap((row) => Object.values(row)) ?? []
    return !isEqual(initialValues, currentValues)
    // NOTE: 第2引数はブラウザのページ離脱確認ダイアログを表示するために必要（カスタムメッセージは表示されず、ブラウザ固有の定型文が表示される）
  }, 'Alert')
  const [showModal, setShowModal] = useState(false)

  // calculationItemsの長さを追跡して、変更時のみrowsを更新
  const prevCalculationItemsLengthRef = useRef<number>(0)
  // useEffect内でeditOCRFormatsを参照しつつ、依存配列に含めない（二重実行防止）
  const editOCRFormatsRef = useRef(editOCRFormats)
  editOCRFormatsRef.current = editOCRFormats

  // Set and get rows
  useEffect(() => {
    if (!dsls || !calculationItems) {
      prevCalculationItemsLengthRef.current = 0
      return
    }
    // 初回または項目数が変わった場合のみ更新
    const needsUpdate =
      prevCalculationItemsLengthRef.current !== calculationItems.length
    if (!needsUpdate) {
      if (isLoading) setIsLoading(false)
      return
    }
    prevCalculationItemsLengthRef.current = calculationItems.length

    // ケースにより変数のデータの値が異なる
    // - restoredEditOCRFormats: APIのレスポンスを加工したEditOCRFormats
    // - editOCRFormats: ReduxのStateからselectしたEditOCRFormats
    //
    // 1. 新規作成時
    //  -  restoredEditOCRFormatsが空
    //  -  editOCRFormatsが空
    // 2. 新規作成時でStep3まで進んで戻ったとき
    //  -  restoredEditOCRFormatsが空
    //  -  editOCRFormatsが値あり
    // 3. 編集時
    //  -  restoredEditOCRFormatsが値あり
    //  -  editOCRFormatsが空
    // 4. 編集時でStep3まで進んで戻ったとき
    //  -  restoredEditOCRFormatsが値あり
    //  -  editOCRFormatsが値あり
    const restoredEditOCRFormats: EditOCRFormatPayload[] =
      EditOCRFormatFunctions.convertDSLToEditOCRFormat(dsls, editReadItems)

    const currentEditOCRFormats = editOCRFormatsRef.current
    const updatedRows = EditOCRFormatFunctions.rows(
      calculationItems,
      editReadItems,
      currentEditOCRFormats,
      restoredEditOCRFormats
    )
    if (!currentEditOCRFormats || currentEditOCRFormats.length === 0) {
      onUpdateEditOCRFormats(restoredEditOCRFormats)
    }
    setRows(updatedRows)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoading は依存配列に含めると二重実行されるため意図的に除外
  }, [calculationItems, onUpdateEditOCRFormats, dsls, editReadItems])

  const setDefaultValues = useCallback(() => {
    editOCRFormats.forEach((editOCRFormat: EditOCRFormatPayload, index) => {
      EditOCRFormatFunctions.getFormNameWithValues(
        index,
        editOCRFormat
      ).forEach((formNameWithValue) => {
        const { name, value } = formNameWithValue
        formMethods.setValue(name, value)
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods は useForm の戻り値で参照が安定しているため除外
  }, [editOCRFormats])

  // TODO: react-hook-form の Controller 登録完了前に getValues() が空を返す可能性がある。watch や setTimeout でフォーム描画完了を待つ方式への改善を検討する
  const initialValuesSetRef = useRef(false)
  useEffect(() => {
    if (!rows.length) return
    if (initialValuesSetRef.current) return

    setDefaultValues()
    const values = formMethods
      .getValues()
      .rows?.flatMap((row) => Object.values(row))
    if (values && values.length > 0) {
      setInitialValues(values)
      initialValuesSetRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods は useForm の戻り値で参照が安定しているため除外
  }, [rows.length, setDefaultValues])

  const getOCRFormats = (): EditOCRFormatPayload[] => {
    const rowsValue = EditOCRFormatFunctions.convertOCRFormatsToApiVal(
      formMethods.getValues().rows
    )
    return editReadItems.map((editReadItem, index) => {
      const rowValue = rowsValue[index]
      return {
        ...editReadItem,
        ...(rowValue ?? {}),
      } as EditOCRFormatPayload
    })
  }

  const handleClickClose = () => {
    setShowModal(true)
  }

  const handleClickGoBack = () => {
    const ocrFormats: EditOCRFormatPayload[] = getOCRFormats()
    // ここで上位にわたす。
    onClickGoBack(ocrFormats)
  }

  // NOTE: 同名称項目「あり」だが、名称が選択されてない場合、false
  const validateOCRFormats = (values: EditOCRFormatPayload[]): boolean => {
    if (values.length === 0) return true
    return !values.some((value) => {
      return value.isDuplicated && !value.sectionName
    })
  }

  const handleClickGoForward = () => {
    const ocrFormats: EditOCRFormatPayload[] = getOCRFormats()
    if (!validateOCRFormats(ocrFormats)) {
      toastContext.sendToast({
        variant: 'error',
        title:
          '同名称項目が有りの場合は、セクション名を記入する必要があります。',
      })
      return
    }
    onClickGoForward(ocrFormats)
  }

  const handleClickCancel = () => {
    setShowModal(false)
  }

  const handleChangeSelect = (
    option: Option,
    rowIndex: number,
    name: string
  ) => {
    if (name.endsWith('isDuplicated')) {
      setRows((prevRows) =>
        prevRows.map((row, index) =>
          index === rowIndex
            ? { ...row, showInput: Boolean(option.value) }
            : row
        )
      )
    }
  }

  const renderContent = () => {
    return (
      <Box className={styles.content}>
        <Typography className={styles.title} variant="h1">
          {`レシート読み取りの準備です。\n各項目の数値の詳細情報を設定しましょう。`}
        </Typography>
        <EditOCRFormatTable
          className={styles.table}
          columns={EditOCRFormatFunctions.columns()}
          rows={rows}
          onChangeSelect={handleChangeSelect}
        />
        <Box className={styles.buttonWrapper}>
          <Button
            title="つぎへ"
            onClick={handleClickGoForward}
            color="primary"
            size="medium"
          />
          <Typography
            color="primary"
            className={styles.previousTypography}
            onClick={handleClickGoBack}
            component="a"
            variant="subtitle1"
          >
            前ページに戻る
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form>
        <Box className={styles.root}>
          <Box className={styles.header} component="header">
            <BreadcrumbsPipe
              className={styles.breadcrumb}
              paths={breadcrumbs}
            />
            <Box className={styles.closeButtonWrapper}>
              <IconButton
                className={styles.closeButton}
                onClick={handleClickClose}
                size="large"
              >
                <Icon icon="close" />
              </IconButton>
            </Box>
          </Box>
          <Box className={styles.contentWrapper}>
            {isLoading ? <CenteringLoading /> : renderContent()}
            <Modal // `disableBackdropClick` is removed by codemod.
              // You can find more details about this breaking change in [the migration guide](https://mui.com/guides/migration-v4/#modal)
              open={showModal}
              title={`今中断するとこれまでの設定内容は\n失われてしまいますがよろしいですか？`}
              agreeButtonTitle="このまま中断する"
              disagreeButtonTitle="設定に戻る"
              onClickAgree={onClickConfirmClose}
              onClickDisagree={handleClickCancel}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}

export default EditOCRFormat
